import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, CreditCard, ShieldCheck, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, gbp, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const shipping = 0;
  const total = subtotal;
  const [placing, setPlacing] = useState(false);

  const payWithStripe = async () => {
    setPlacing(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({
          product_id: i.product_id, slug: i.slug, name: i.name,
          price: i.price, qty: i.qty, image: i.image,
        })),
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Could not start checkout. Please try again.");
        setPlacing(false);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPlacing(false);
    }
  };

  if (items.length === 0)
    return <div className="max-w-2xl mx-auto px-4 py-24 text-center"><p className="text-muted-foreground">Your basket is empty.</p><Link to="/" className="mt-4 inline-block text-primary underline">Go shopping</Link></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <h1 className="text-3xl font-display font-black tracking-tighter mb-8 flex items-center gap-2"><Lock className="w-6 h-6 text-primary" /> Secure Checkout</h1>
      <div className="grid lg:grid-cols-[1fr_380px] gap-8">
        <div className="space-y-6" data-testid="checkout-review">
          {!user && (
            <div className="bg-primary/5 border border-primary/20 rounded-sm p-4 text-sm" data-testid="checkout-guest-note">
              Checking out as a guest — no account needed. Prefer to keep track of your orders? <Link to="/account" className="font-semibold underline">Create an account</Link> (optional).
            </div>
          )}

          <section className="bg-card border border-border rounded-sm p-6" data-testid="delivery-method">
            <h2 className="font-display font-bold text-lg mb-2 flex items-center gap-2"><Truck className="w-5 h-5 text-primary" /> Delivery</h2>
            <p className="text-sm font-semibold text-primary">FREE tracked delivery on all orders — Royal Mail Tracked 48 🎉</p>
            <p className="text-xs text-muted-foreground mt-1">Your UK delivery address is collected securely on the payment page.</p>
          </section>

          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="font-display font-bold text-lg mb-2 flex items-center gap-2"><CreditCard className="w-5 h-5 text-primary" /> Payment</h2>
            <p className="text-sm text-muted-foreground">
              You'll be taken to our secure Stripe payment page to complete your order. Your delivery address is collected there.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /> Encrypted card payment powered by Stripe</li>
              <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /> Apple Pay &amp; Google Pay supported</li>
              <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /> UK delivery address captured at payment</li>
            </ul>
          </section>

          <button type="button" onClick={payWithStripe} disabled={placing} data-testid="pay-stripe-btn"
            className="w-full bg-accent text-white py-4 rounded-sm font-bold text-lg hover:bg-accent/90 disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
            <Lock className="w-5 h-5" /> {placing ? "Redirecting to Stripe…" : `Pay ${gbp(total)}`}
          </button>
        </div>

        <aside className="bg-card border border-border rounded-sm p-6 h-fit lg:sticky lg:top-28">
          <h2 className="font-display font-bold text-lg mb-4">Your Order</h2>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {items.map((it) => (
              <div key={it.product_id} className="flex gap-3 text-sm">
                <img src={it.image} alt="" className="w-12 h-12 object-cover rounded-sm border border-border" />
                <div className="flex-1 min-w-0"><p className="line-clamp-1 font-medium">{it.name}</p><p className="text-muted-foreground">Qty {it.qty}</p></div>
                <span className="font-semibold">{gbp(it.price * it.qty)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-border mt-4 pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{gbp(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span className="font-semibold text-primary">FREE</span></div>
            <div className="flex justify-between font-display font-bold text-lg pt-1"><span>Total</span><span>{gbp(total)}</span></div>
          </div>
        </aside>
      </div>
    </div>
  );
}
